import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button as AriaButton } from "react-aria-components";
import { Icon, images } from "@core/assets";
import { cn } from "@core/utils";
import { Tooltip } from "../ui/Tooltip";
import type { NavGroup, NavItem } from "./types";

interface FrameSidebarProps {
  /** Labelled nav sections, already filtered to what this account may see. */
  groups: NavGroup[];
  /** Icon-only rail (desktop). The drawer never collapses. */
  collapsed?: boolean;
  /** When given, the « Réduire le menu » control shows at the bottom and calls this. */
  onToggleCollapsed?: () => void;
  /** Called after a link is activated: the mobile drawer closes itself with it. */
  onNavigate?: () => void;
  className?: string;
}

// Figma "Sidebar": an active entry is a raised white card with a hairline border and a menthe
// icon; the rest are flat. `py-3 lg:py-2` keeps a 44px touch target in the drawer.
const LINK = cn(
  "group flex items-center gap-sm rounded-md border border-transparent px-sm py-3 text-sm-medium lg:py-2",
  "text-(--text-secondary) transition-colors",
  "hover:bg-ink-50",
  "outline-none focus-visible:ring-2 focus-visible:ring-focus-ring",
  "data-[status=active]:border-(--border-default) data-[status=active]:bg-surface-card",
  "data-[status=active]:text-(--text-primary) data-[status=active]:shadow-xs"
);

function NavLink({
  item,
  collapsed,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation("common");
  const label = t(item.labelKey);

  return (
    <li>
      <Tooltip content={label} isDisabled={!collapsed} placement="right">
        <Link
          to={item.to}
          onClick={onNavigate}
          aria-label={collapsed ? label : undefined}
          className={cn(LINK, collapsed && "justify-center px-0")}
        >
          <Icon
            name={item.icon}
            size={20}
            className="shrink-0 group-data-[status=active]:text-menthe-700"
          />
          {collapsed ? null : <span className="truncate">{label}</span>}
        </Link>
      </Tooltip>
      {item.expanded}
    </li>
  );
}

function NavSection({
  group,
  collapsed,
  onNavigate,
}: {
  group: NavGroup;
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(true);
  // The rail has no room for headings, so every entry stays visible whatever the group state.
  // A group with no heading (« Cohortes ») has nothing to fold: its items always show.
  const { labelKey } = group;
  const showItems = collapsed || open || !labelKey;

  return (
    <div className="flex flex-col gap-xxs">
      {collapsed || !labelKey ? null : (
        <AriaButton
          onPress={() => setOpen((value) => !value)}
          aria-expanded={open}
          className={cn(
            "flex items-center justify-between rounded-sm px-sm py-xs text-xs-medium text-(--text-tertiary)",
            "outline-none focus-visible:ring-2 focus-visible:ring-focus-ring"
          )}
        >
          {t(labelKey)}
          <Icon
            name="chevron-down"
            size={16}
            className={cn(
              "transition-transform motion-reduce:transition-none",
              !open && "-rotate-90"
            )}
          />
        </AriaButton>
      )}
      {showItems ? (
        <ul className="flex flex-col gap-xxs">
          {group.items.map((item) => (
            <NavLink
              key={item.to}
              item={item}
              collapsed={collapsed}
              onNavigate={onNavigate}
            />
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * The staff frame's sidebar (Figma "Sidebar"): logo, labelled nav groups (a group folds), and the
 * collapse control at the bottom. Shared verbatim by the desktop `<aside>` and the mobile drawer,
 * so the two cannot drift. It renders whatever groups it is handed and knows no route.
 *
 * Collapsed (desktop only) it becomes an icon rail with tooltips. The rail is not drawn in Figma:
 * 64px wide, the logo mark only, headings hidden.
 */
export function FrameSidebar({
  groups,
  collapsed = false,
  onToggleCollapsed,
  onNavigate,
  className,
}: FrameSidebarProps) {
  const { t } = useTranslation("common");
  // A group whose entries were all filtered out must not leave its heading behind.
  const visibleGroups = groups.filter((group) => group.items.length > 0);
  const toggleLabel = t(
    collapsed ? "layout.expand-menu" : "layout.collapse-menu"
  );

  return (
    <div
      className={cn(
        "flex h-full flex-col gap-lg px-md py-md",
        collapsed && "items-center px-xs",
        className
      )}
    >
      {/* "DZ Champions" is a proper noun: not translated. The rail shows the mark only (the
          wordmark file's leftmost part) until a standalone mark asset exists. */}
      <div
        className={cn(
          "flex h-8 shrink-0 items-center px-xs",
          collapsed && "w-7 overflow-hidden px-0"
        )}
      >
        <img
          src={images["logo-primary"]}
          alt="DZ Champions"
          className="h-8 w-auto max-w-none"
        />
      </div>

      <nav
        aria-label={t("layout.main-navigation")}
        className={cn(
          "flex min-h-0 flex-1 flex-col gap-lg overflow-y-auto",
          collapsed && "w-full"
        )}
      >
        {visibleGroups.map((group, index) => (
          <NavSection
            key={group.labelKey ?? `group-${String(index)}`}
            group={group}
            collapsed={collapsed}
            onNavigate={onNavigate}
          />
        ))}
      </nav>

      {onToggleCollapsed ? (
        <Tooltip
          content={toggleLabel}
          isDisabled={!collapsed}
          placement="right"
        >
          <AriaButton
            onPress={onToggleCollapsed}
            aria-label={collapsed ? toggleLabel : undefined}
            className={cn(
              "flex shrink-0 items-center gap-sm rounded-md px-sm py-2 text-sm-medium text-(--text-secondary)",
              "outline-none transition-colors hover:bg-ink-50 focus-visible:ring-2 focus-visible:ring-focus-ring",
              collapsed && "justify-center px-0"
            )}
          >
            <Icon
              name={collapsed ? "panel-left-open" : "panel-left-close"}
              size={20}
              className="shrink-0"
            />
            {collapsed ? null : toggleLabel}
          </AriaButton>
        </Tooltip>
      ) : null}
    </div>
  );
}
